import type { ReactNode } from 'react';
import { Ban, CloudOff, Inbox, ServerCrash, ShieldAlert } from 'lucide-react';
import { Button, Card, EmptyState } from '@eombor/ui';
import { ApiClientError } from '../api/client.js';
import { t } from '../i18n/index.js';

/**
 * Xato TURI — foydalanuvchi uchun ma'noli farq.
 *
 * Status kodining o'zi hech narsa aytmaydi; odamga «nima bo'ldi va endi
 * nima qilay» degan javob kerak, va u har turda BOSHQACHA:
 * biriktirilmagan bo'lim uchun kadrlar bo'limiga borish kerak, tarmoq
 * uzilganda esa shunchaki qayta urinish yetadi.
 */
type Kind = 'offline' | 'forbidden' | 'missing' | 'server' | 'unknown';

const kindOf = (error: unknown): Kind => {
  if (!(error instanceof ApiClientError)) {
    /* Tarmoq uzilganda `fetch` `TypeError` tashlaydi va u bu yergacha
       yetib keladi — server javob bermagani ham xabarning bir turi. */
    return 'offline';
  }
  if (error.status === 403 || error.status === 401) return 'forbidden';
  if (error.status === 404) return 'missing';
  if (error.status >= 500) return 'server';
  return 'unknown';
};

const ICONS: Record<Kind, ReactNode> = {
  offline: <CloudOff size={20} aria-hidden />,
  forbidden: <ShieldAlert size={20} aria-hidden />,
  missing: <Inbox size={20} aria-hidden />,
  server: <ServerCrash size={20} aria-hidden />,
  unknown: <Ban size={20} aria-hidden />,
};

const TITLE_KEYS: Record<Kind, string> = {
  offline: 'apiError.offlineTitle',
  forbidden: 'apiError.forbiddenTitle',
  missing: 'apiError.missingTitle',
  server: 'apiError.serverTitle',
  unknown: 'apiError.unknownTitle',
};

/**
 * SERVER MATNI ishlatiladimi.
 *
 * 4xx javoblarida backend odamga qaratilgan aniq jumla yozadi («Sizga
 * bo'lim biriktirilmagan — kadrlar bo'limiga murojaat qiling») va uni
 * umumiy matn bilan almashtirish ma'lumot yo'qotish bo'lardi.
 *
 * 5xx da esa aksincha: u yerdagi matn ichki nosozlik haqida bo'ladi va
 * foydalanuvchiga hech narsa bermaydi.
 */
const descriptionOf = (error: unknown, kind: Kind): string => {
  if (kind === 'server' || kind === 'offline') return t(`${TITLE_KEYS[kind]}Hint`);
  return error instanceof ApiClientError && error.message !== ''
    ? error.message
    : t(`${TITLE_KEYS[kind]}Hint`);
};

export interface ApiErrorStateProps {
  error: unknown;
  /** Qayta urinish — berilmasa tugma chizilmaydi. */
  onRetry?: () => void;
  /**
   * Sarlavhani sahifa O'ZI aytadi.
   *
   * Umumiy «Ma'lumot topilmadi» to'g'ri, lekin kambag'al: «Mening
   * omborim» da 404 ning ma'nosi aniq — bo'lim biriktirilmagan — va
   * sahifa buni umumiy komponentdan yaxshiroq biladi. Faqat 4xx da
   * hisobga olinadi: tarmoq uzilishi yoki 500 sahifaga bog'liq emas va
   * u yerda sahifaning taxmini yolg'on bo'lardi.
   */
  title?: string;
}

/**
 * API xatosining ODAMGA qaratilgan ko'rinishi.
 *
 * Texnik tafsilot (status, kod, JSON, stek) EKRANGA chiqmaydi: u
 * foydalanuvchiga hech narsa bermaydi va uni qo'rqitadi. Kerak bo'lsa
 * tarmoq yorlig'ida ko'rinadi.
 *
 * Ba'zi «xato» lar aslida oddiy hol: bo'limi yo'q xodimga 404 kelishi —
 * nosozlik emas, javob. Shuning uchun ko'rinish ham tinch: qizil emas,
 * ikonka va nima qilish kerakligi.
 */
export const ApiErrorState = ({ error, onRetry, title }: ApiErrorStateProps) => {
  const kind = kindOf(error);
  /* Sahifaning sarlavhasi faqat 4xx da ishlatiladi: qolganida u
     haqiqatga mos kelmasdi — server yiqilgani «bo'lim biriktirilmagan»
     degani emas. */
  const heading =
    title !== undefined && (kind === 'missing' || kind === 'forbidden')
      ? title
      : t(TITLE_KEYS[kind]);

  return (
    <Card>
      <EmptyState
        icon={ICONS[kind]}
        title={heading}
        description={descriptionOf(error, kind)}
        {...(onRetry === undefined
          ? {}
          : {
              action: (
                <Button variant="secondary" onClick={onRetry}>
                  {t('apiError.retry')}
                </Button>
              ),
            })}
      />
    </Card>
  );
};
